import React, { useState } from 'react';
import router from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { asyncActions, serialActions } from '@docavenue/core';
import { useCurrentUser } from '@docavenue/components';
import { get } from 'lodash';
import { Box, Button } from '@mui/material';
import { Document } from '@maiia/model/generated/model/api-patient/api-patient';
import { useTranslation } from '@/src/i18n';
import { analyticsEvent } from '@/src/analytic_tag_manager';

import {
  authActions,
  documentsActions,
  favoritePractitionersActions,
  shareDocumentsActions,
  usersActions,
} from '@/src/actions';

import ShareDocumentInitial from './ShareDocumentInitial';
import ShareDocumentChoose from './ShareDocumentChoose';
import ShareDocumentUpload from './ShareDocumentUpload';
import { enqueueSnackbar } from '@/src/actions/snackbar';
import { APP_ROUTES } from '@/src/constants';
import {
  DialogSteps,
  EndSharingType,
  ErrorObject,
} from '@/components/templates/TlcAmc/utils';

const ShareDocumentController = ({ pharmacy }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const userProfiles = useSelector(state =>
    get(
      state,
      'authentication.item.userPatientInformation.userPatientProfiles',
    ),
  );

  const [currentDialog, setCurrentDialog] = useState<DialogSteps>(null);

  const endSharing: EndSharingType = (result, error): void => {
    setCurrentDialog(null);

    if (result === 'fail' || result === 'success') {
      dispatch(
        enqueueSnackbar({
          message: error
            ? t(
                [
                  error.subCodes?.[0]?.toLowerCase(),
                  error.code?.toLowerCase(),
                  error.message?.toLowerCase(),
                ].map(key => `error:${key}`),
              )
            : t(`document_share_success`),
          severity: error ? 'error' : 'success',
          duration: 5000,
        }),
      );
    }
  };

  const addPharmacyToFavorites = async () => {
    const values = userProfiles?.map(({ id, firstName }) => ({
      profileId: id,
      firstName,
      type: 'FAVORITE',
    }));

    try {
      await asyncActions(
        dispatch,
        favoritePractitionersActions.updateManyPost(values, {
          params: {
            centerId: pharmacy.id,
          },
        }),
      );

      const user = await asyncActions(dispatch, usersActions.getOne('me'));
      dispatch(
        authActions.setItem({
          ...currentUser,
          ...user,
        }),
      );

      endSharing('success');
    } catch (error) {
      endSharing('fail', error as ErrorObject);
    }
  };

  const shareDocument = async (document: Document) => {
    try {
      await asyncActions(
        dispatch,
        shareDocumentsActions.create({
          documentId: document.id,
          destinationPractitioners: [
            {
              centerId: pharmacy.id,
            },
          ],
        }),
      );

      addPharmacyToFavorites();
    } catch (error) {
      endSharing('fail', error as ErrorObject);
    }
  };

  const uploadDocument = values => {
    dispatch(
      serialActions.serial([
        () => {
          const action = documentsActions.upload({
            file: values.file,
            document: JSON.stringify({
              profileId: values.profileId,
              name: values.title,
              category: values.category,
            }),
          });

          action.onSuccess = document => {
            shareDocument(document);
          };

          return action;
        },
      ]),
    );
  };

  const dialogs = {
    initial: (
      <ShareDocumentInitial
        goToUpload={() => setCurrentDialog('upload')}
        goToChoose={() => setCurrentDialog('choose')}
        dialogTitle={t('share-prescription-initial-title', {
          pharmacyName: pharmacy.name,
        })}
        endSharing={endSharing}
      />
    ),
    upload: (
      <ShareDocumentUpload
        uploadDocument={uploadDocument}
        endSharing={endSharing}
      />
    ),
    choose: (
      <ShareDocumentChoose
        shareDocument={shareDocument}
        endSharing={endSharing}
        dialogTitle={t('share-prescription-choose-title')}
      />
    ),
  };

  const onSharePrescriptionClick = () => {
    analyticsEvent({
      category: 'Documents',
      action: 'ShareDocument',
      label: 'pharmacy page',
    });

    return userProfiles
      ? setCurrentDialog('initial')
      : router.push({
          pathname: APP_ROUTES.LOGIN,
          query: { next: pharmacy.url, fromPreferences: true },
        });
  };

  return (
    <>
      <Box mt={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={onSharePrescriptionClick}
        >
          {t('share-your-prescription')}
        </Button>
      </Box>
      {currentDialog && dialogs[currentDialog]}
    </>
  );
};

export default ShareDocumentController;
